import { FC, useCallback, useContext } from 'react';
import { Prompt } from 'react-router-dom';
import { isValid as isValidDate } from 'date-fns';
import { Registration } from '@stax-core/types-omni';
import { RegistrationForm } from '../../@types';
import { coreapi } from '../../api';
import {
  Checkbox,
  DateInput,
  Field,
  FieldError,
  FormDivider,
  Group,
  Input,
  ToggleableField,
  ToggleableFieldProps,
} from '../../components';
import {
  AuthStore,
  RegistrationStore,
  updateRegistration,
} from '../../context';
import {
  useToaster,
  useWriteableFieldFormValidation,
} from '../../hooks';
import { BackButton } from '../components/BackButton';
import { NextButton } from '../components/NextButton';
import CenteredContent from '../components/CenteredContent';
import { isMeaningfulString } from '../../util/validator.util';
import { formatFederalTaxId } from '../../util/format.util';
import { MainForm } from '../components/application/MainForm';

interface FieldValues
  extends Pick<
    Registration,
    | 'business_legal_name'
    | 'business_dba'
    | 'business_open_date'
    | 'business_tax_id'
  > {
  showDbaName: boolean;
  businessHasEIN: boolean;
}

export interface BusinessDetailsProps extends RegistrationForm<FieldValues> {}

const BusinessDetailsFields: FC<BusinessDetailsProps> = props => {
  const { form: { mutators }, values } = props;

  const defaultFieldProps: Partial<ToggleableFieldProps<any, HTMLElement>> = {
    toggleable: props.mode === 'review',
    defaultMode: props.mode === 'review' ? 'display' : 'edit',
  };

  return (
    <>
      <div data-testid="business-details-1">
        <Group direction="column">
          <ToggleableField
            name="business_legal_name"
            label="Legal Business Name"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} />}
          </ToggleableField>
          <FieldError name="business_legal_name" />
        </Group>

        <Group direction="column" style={{ margin: '0 0 0.5rem 0' }}>
          <Field
            name="showDbaName"
            label="Legal Business Name"
            type="checkbox"
            {...defaultFieldProps}
          >
            {props => (
              <Checkbox
                {...props.input}
                label="My business is known by a different name"
                id="showDbaName"
                onChange={evt => {
                  props.input.onChange(evt);
                  if (!evt.target.checked) {
                    mutators.setFieldValue('business_dba', '');
                  }
                  return true;
                }}
              />
            )}
          </Field>
          <FieldError name="showDbaName" />
        </Group>

        {values.showDbaName && (
          <Group direction="column">
            <ToggleableField
              name="business_dba"
              label="DBA Name"
              {...defaultFieldProps}
            >
              {props => <Input {...props.input} />}
            </ToggleableField>
            <FieldError name="business_dba" />
          </Group>
        )}

        <FormDivider />

        <Group direction="column">
          <ToggleableField
            name="business_open_date"
            label="Date Incorporated Business"
            {...defaultFieldProps}
          >
            {props => <DateInput {...props.input} />}
          </ToggleableField>
          <FieldError name="business_open_date" />
        </Group>

        <Group direction="column" style={{ margin: '0 0 0.5rem 0' }}>
          <Field
            name="businessHasEIN"
            label="Business EIN"
            type="checkbox"
            {...defaultFieldProps}
          >
            {props => (
              <Checkbox
                {...props.input}
                label="My business has an EIN"
                id="businessHasEIN"
                onChange={evt => {
                  props.input.onChange(evt);
                  if (!evt.target.checked) {
                    mutators.setFieldValue('business_tax_id', '');
                  }
                  return true;
                }}
              />
            )}
          </Field>
          <FieldError name="businessHasEIN" />
        </Group>

        {values.businessHasEIN && (
          <Group direction="column">
            <ToggleableField
              name="business_tax_id"
              label="Business EIN"
              parse={formatFederalTaxId}
              format={formatFederalTaxId}
              {...defaultFieldProps}
            >
              {props => (
                <Input
                  {...props.input}
                  autoComplete="off"
                  tooltip="Tax ID/EIN is required but under some circumstances an applicant may use their SSN instead."
                />
              )}
            </ToggleableField>
            <FieldError name="business_tax_id" />
          </Group>
        )}
      </div>
      {props.children}
    </>
  );
};

export const BusinessDetailsPage: FC = () => {
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { toaster, toast } = useToaster();
  const {
    state: { auth },
  } = useContext(AuthStore);
  const authToken = auth?.token ?? null;

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      const payload: Partial<Registration> = {
        business_legal_name: formValues.business_legal_name,
        business_dba: formValues.business_dba,
        business_open_date: formValues.business_open_date,
        business_tax_id: formValues.businessHasEIN
          ? formValues.business_tax_id
          : null,
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toast, toaster],
  );

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};
    if (!isMeaningfulString(values.business_legal_name)) {
      errors.business_legal_name = 'Not a valid name';
    }
    if (values.showDbaName) {
      if (!isMeaningfulString(values.business_dba)) {
        errors.business_dba = 'Not a valid name';
      }
    }
    if (
      !values.business_open_date ||
      !isValidDate(new Date(values.business_open_date))
    ) {
      errors.business_open_date = 'Not a valid date';
    }

    if (
      values.businessHasEIN &&
      (/[^0-9\-]/.test(values.business_tax_id!) ||
        values.business_tax_id!.replace(/[^0-9]/g, '').length !== 9)
    ) {
      errors.business_tax_id =
        'Invalid tax ID. Please enter your 9 digit tax ID number.';
    }
    return errors;
  });

  return (
    <MainForm<FieldValues>
      initialValues={mapInitialFormValues(registration)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CenteredContent
          id="business-details"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="business-details-back" />
          <h2>Business details</h2>
          <div>
            <p>
              Please provide the following legal information about your
              business.
            </p>
            <BusinessDetailsFields mode="edit" {...formProps}>
              <Prompt
                when={formProps.dirty && !formProps.submitting}
                message="You have unsaved changes; are you sure you want to leave without saving?"
              />
            </BusinessDetailsFields>
          </div>
          <NextButton id="business-details-continue" formProps={formProps} />
        </CenteredContent>
      )}
    </MainForm>
  );
};

function mapInitialFormValues(
  registration: Registration | null,
): Partial<FieldValues> {
  /** Copied from BusinessFields.util mapRegistrationToInitialFormValues() */
  return registration
    ? {
        business_legal_name: registration.business_legal_name,
        business_dba: registration.business_dba,
        business_open_date: registration.business_open_date,
        business_tax_id: registration.business_tax_id,
        showDbaName: !!registration.business_dba,
        businessHasEIN: !!registration.business_tax_id,
      }
    : ({} as any);
}
